import { useEffect, useState } from "react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, ResponsiveContainer,
  Tooltip, XAxis, YAxis, CartesianGrid, Cell,
} from "recharts";
import {
  Users, UserPlus, Receipt, Trophy, Clock, CalendarBlank, SignIn, Pulse,
  ShieldCheck, ShieldWarning, Warning, ArrowClockwise, Gauge, GlobeHemisphereWest,
  TrendUp, CurrencyCircleDollar, Gift, PaperPlaneTilt, Coins, Wallet, SealCheck,
  ClockCountdown, XCircle, CheckCircle, CaretRight,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { Card, Loading } from "@/components/riq";
import { sourceMeta } from "@/components/SourceBadge";
import { useAdminNav } from "@/pages/adminNav";
import { toast } from "sonner";

const zar = (v) => money(v, "ZAR");
const FULL_PERIODS = [
  { k: "today", l: "Today" }, { k: "7d", l: "7 days" }, { k: "30d", l: "30 days" },
  { k: "90d", l: "90 days" }, { k: "all", l: "All time" },
];
const ACT_PERIODS = [
  { k: "7d", l: "7 days" }, { k: "30d", l: "30 days" }, { k: "90d", l: "90 days" }, { k: "all", l: "All time" },
];

const SECTIONS = [
  { id: "sec-behaviour", label: "User behaviour", icon: Users, accent: "#6E3FFC" },
  { id: "sec-scans", label: "Scan activity", icon: Receipt, accent: "#42C97C" },
  { id: "sec-activity", label: "Activity", icon: Pulse, accent: "#2BB0C4" },
  { id: "sec-warranties", label: "Warranties", icon: ShieldCheck, accent: "#F5A623" },
  { id: "sec-rewards", label: "Rewards", icon: Gift, accent: "#B36EFF" },
];

export default function AdminReports() {
  return (
    <div className="space-y-8" data-testid="admin-reports">
      <AnomalyPanel />
      <JumpNav />
      <UserBehaviourSection />
      <ScanActivitySection />
      <ActivitySection />
      <WarrantySection />
      <RewardsSection />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Section scaffolding                                                 */
/* ------------------------------------------------------------------ */

function JumpNav() {
  const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <div
      data-testid="dashboard-jump-nav"
      className="sticky top-0 z-20 -mx-1 flex flex-wrap gap-2 rounded-2xl border border-riq-border bg-white/80 px-2 py-2 backdrop-blur-xl"
    >
      {SECTIONS.map((s) => (
        <button
          key={s.id}
          data-testid={`jump-${s.id}`}
          onClick={() => jump(s.id)}
          className="group flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-medium text-neutral-600 transition-colors hover:bg-riq-sage/60 hover:text-riq-primary"
        >
          <span className="grid h-6 w-6 place-items-center rounded-lg" style={{ background: `${s.accent}1a`, color: s.accent }}>
            <s.icon size={14} weight="fill" />
          </span>
          {s.label}
        </button>
      ))}
    </div>
  );
}

function SectionShell({ id, index, icon: Icon, accent, title, subtitle, period, setPeriod, periods, children }) {
  return (
    <section id={id} data-testid={id} className="scroll-mt-16 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-3.5">
          <div
            className="relative grid h-12 w-12 shrink-0 place-items-center rounded-2xl"
            style={{ background: `${accent}14`, color: accent, boxShadow: `inset 0 0 0 1px ${accent}33` }}
          >
            <Icon size={24} weight="duotone" />
            <span
              className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full text-[10px] font-bold text-white"
              style={{ background: accent }}
            >
              {index}
            </span>
          </div>
          <div>
            <h2 className="font-head text-xl font-bold tracking-tight text-riq-primary">{title}</h2>
            <p className="text-sm text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        {periods && (
          <div className="flex flex-wrap items-center gap-1.5">
            {periods.map((p) => {
              const on = period === p.k;
              return (
                <button
                  key={p.k}
                  data-testid={`${id}-period-${p.k}`}
                  onClick={() => setPeriod(p.k)}
                  className="rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors"
                  style={on
                    ? { background: accent, color: "#fff" }
                    : { background: "transparent", color: "#64748b", boxShadow: "inset 0 0 0 1px var(--riq-border, #e9eaf2)" }}
                >
                  {p.l}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {children}
    </section>
  );
}

function usePeriodData(url, period) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    setData(null);
    api.get(`${url}${url.includes("?") ? "&" : "?"}period=${period}`)
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData(false));
    return () => { alive = false; };
  }, [url, period]);
  return data;
}

/* ------------------------------------------------------------------ */
/* 1 — User behaviour                                                  */
/* ------------------------------------------------------------------ */

function UserBehaviourSection() {
  const [period, setPeriod] = useState("30d");
  const data = usePeriodData("/admin/reports", period);
  const accent = "#6E3FFC";
  return (
    <SectionShell
      id="sec-behaviour" index={1} icon={Users} accent={accent}
      title="User behaviour" subtitle="Who's joining, and how they scan"
      period={period} setPeriod={setPeriod} periods={FULL_PERIODS}
    >
      {data === false ? <ErrorBox label="Could not load user behaviour." /> : !data ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi accent={accent} icon={Users} label="Total users" value={data.kpis.total_users} sub={`+${data.kpis.signups_today} today · +${data.kpis.signups_7d} this week`} testId="ub-kpi-total" />
            <Kpi accent={accent} icon={UserPlus} label="New today" value={data.kpis.signups_today} sub={`${data.kpis.scanners_today} scanned today`} testId="ub-kpi-today" />
            <Kpi accent={accent} icon={UserPlus} label="New this week" value={data.kpis.signups_7d} sub="last 7 days" testId="ub-kpi-7d" />
            <Kpi accent={accent} icon={UserPlus} label="New (30 days)" value={data.kpis.signups_30d} sub={`${data.kpis.receipts_total} receipts all-time`} testId="ub-kpi-30d" />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <TrendCard title="Signups over time" data={data.signups_daily} dataKey="count" color={accent} icon={UserPlus} />
            </div>
            <RankCard title="Top scanners" icon={Trophy} accent={accent} rows={data.top_scanners.map((s) => ({
              primary: s.name || s.email, secondary: `${s.receipts} receipts`, value: zar(s.gross),
            }))} testId="ub-top-scanners" />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <ChartCard title="Busiest hours" icon={Clock} testId="ub-by-hour">
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={data.by_hour}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f4" />
                  <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={1} tickFormatter={(h) => `${h}h`} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip formatter={(v) => [v, "receipts"]} labelFormatter={(h) => `${h}:00`} />
                  <Bar dataKey="count" fill={accent} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Scans by day of week" icon={CalendarBlank} testId="ub-by-weekday">
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={data.by_weekday}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f4" />
                  <XAxis dataKey="weekday" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip formatter={(v) => [v, "receipts"]} />
                  <Bar dataKey="count" fill="#42C97C" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </>
      )}
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ */
/* 1b — Scan activity (period vs all-time total)                       */
/* ------------------------------------------------------------------ */

function ScanActivitySection() {
  const [period, setPeriod] = useState("today");
  const data = usePeriodData("/admin/reports", period);
  const accent = "#42C97C";
  const periodLabel = ((FULL_PERIODS.find((p) => p.k === period) || {}).l || "period").toLowerCase();
  const k = data && data.kpis;
  return (
    <SectionShell
      id="sec-scans" index={2} icon={Receipt} accent={accent}
      title="Scan activity" subtitle={`Scans ${period === "today" ? "today" : `over ${periodLabel}`} vs all-time total`}
      period={period} setPeriod={setPeriod} periods={FULL_PERIODS}
    >
      {data === false ? <ErrorBox label="Could not load scan activity." /> : !data ? <Loading /> : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <ComparisonCard
            icon={Receipt} accent={accent} label="New scans"
            periodValue={k.scans_period} periodLabel={periodLabel}
            total={k.receipts_total} formatter={(v) => v.toLocaleString()} testId="scan-new"
          />
          <ComparisonCard
            icon={CurrencyCircleDollar} accent="#6E3FFC" label="Gross merchandise value"
            periodValue={k.gross_created_period} periodLabel={periodLabel}
            total={k.gross_total} formatter={zar} testId="scan-gmv"
          />
          <ComparisonCard
            icon={Users} accent="#F5A623" label="Users who scanned"
            periodValue={k.scanners_period} periodLabel={periodLabel}
            total={k.total_scanners} formatter={(v) => v.toLocaleString()} testId="scan-users"
          />
        </div>
      )}
      {data && data !== false && data.by_source && data.by_source.length > 0 && (
        <div className="mt-4" data-testid="admin-source-tally">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">By capture method · {periodLabel}</div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {data.by_source.map(({ source, label, count }) => {
              const m = sourceMeta(source);
              return (
                <Card key={source} data-testid={`admin-source-${source}`} className="flex items-center gap-3 p-4">
                  <div className="grid h-10 w-10 place-items-center rounded-2xl shrink-0" style={{ background: `${m.color}1a`, color: m.color }}>
                    <m.Icon size={18} weight="bold" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-head text-2xl font-bold text-riq-primary leading-none">{count.toLocaleString()}</div>
                    <div className="mt-0.5 text-xs font-medium text-muted-foreground truncate">{label}</div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </SectionShell>
  );
}

function ComparisonCard({ icon: Icon, accent, label, periodValue, periodLabel, total, formatter, testId }) {
  const pct = total > 0 ? Math.min(100, Math.round((periodValue / total) * 100)) : 0;
  return (
    <Card data-testid={testId} className="flex flex-col gap-4 p-5">
      <div className="flex items-center justify-between">
        <div className="grid h-10 w-10 place-items-center rounded-2xl" style={{ background: `${accent}14`, color: accent }}>
          <Icon size={20} weight="duotone" />
        </div>
        <span className="rounded-full px-2.5 py-1 text-[11px] font-bold" style={{ background: `${accent}14`, color: accent }}>
          {pct}% of total
        </span>
      </div>
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="mt-0.5 flex items-baseline gap-2">
          <span className="font-head text-3xl font-bold text-riq-primary" data-testid={`${testId}-period`}>{formatter(periodValue)}</span>
          <span className="text-xs font-medium text-muted-foreground">{periodLabel}</span>
        </div>
      </div>
      <div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-100">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: accent }} />
        </div>
        <div className="mt-1.5 flex items-center justify-between text-xs">
          <span className="text-muted-foreground">of all-time total</span>
          <span className="font-semibold text-neutral-700" data-testid={`${testId}-total`}>{formatter(total)}</span>
        </div>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* 2 — Activity                                                        */
/* ------------------------------------------------------------------ */

function ActivitySection() {
  const { openReceipts } = useAdminNav();
  const [period, setPeriod] = useState("30d");
  const data = usePeriodData("/admin/activity", period);
  const accent = "#2BB0C4";
  return (
    <SectionShell
      id="sec-activity" index={3} icon={Pulse} accent={accent}
      title="Activity" subtitle="Logins and active users over time"
      period={period} setPeriod={setPeriod} periods={ACT_PERIODS}
    >
      {data === false ? <ErrorBox label="Could not load activity." /> : !data ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi accent={accent} icon={SignIn} label="Logins today" value={data.summary.today.logins} sub={`${data.summary.today.active} active users`} testId="act-kpi-today" />
            <Kpi accent={accent} icon={SignIn} label="Logins (7d)" value={data.summary.last_7d.logins} sub={`${data.summary.last_7d.active} active users`} testId="act-kpi-7d" />
            <Kpi accent={accent} icon={SignIn} label="Logins this month" value={data.summary.this_month.logins} sub={`${data.summary.this_month.active} active users`} testId="act-kpi-month" />
            <Kpi accent={accent} icon={Users} label="Active (30d)" value={data.summary.last_30d.active} sub={`${data.summary.last_30d.logins} logins`} testId="act-kpi-active" />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <TrendCard title="Logins over time" data={data.daily} dataKey="logins" color={accent} icon={SignIn} />
            <TrendCard title="Active users per day" data={data.daily} dataKey="active" color="#42C97C" icon={Pulse} />
          </div>

          <RankCard
            title="Most active users" icon={Pulse} accent={accent} testId="act-top-users"
            rows={data.top_active.map((u, i) => ({
              primary: u.name || u.email, secondary: u.area || u.email,
              value: `${u.logins} logins`, onClick: () => openReceipts({ user_id: u.user_id, user_email: u.email }),
              testId: `act-user-link-${i}`,
            }))}
          />
        </>
      )}
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ */
/* 3 — Warranties                                                      */
/* ------------------------------------------------------------------ */

function WarrantySection() {
  const [period, setPeriod] = useState("30d");
  const data = usePeriodData("/admin/warranties", period);
  const accent = "#F5A623";
  const periodLabel = (FULL_PERIODS.find((p) => p.k === period) || {}).l || "period";

  const statusData = data && [
    { name: "Active", key: "active", value: data.status.active, color: "#42C97C", icon: SealCheck },
    { name: "Expiring soon", key: "expiring_soon", value: data.status.expiring_soon, color: "#F5A623", icon: ClockCountdown },
    { name: "Expired", key: "expired", value: data.status.expired, color: "#FF6B6B", icon: XCircle },
  ];
  const delta = data ? data.new_added - (data.prev_added || 0) : 0;

  return (
    <SectionShell
      id="sec-warranties" index={4} icon={ShieldCheck} accent={accent}
      title="Warranties" subtitle="Total tracked vs newly added"
      period={period} setPeriod={setPeriod} periods={FULL_PERIODS}
    >
      {data === false ? <ErrorBox label="Could not load warranties." /> : !data ? <Loading /> : (
        <>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {/* Total vs new-added hero */}
            <Card className="lg:col-span-2 !p-0 overflow-hidden">
              <div className="grid grid-cols-1 divide-y divide-riq-border sm:grid-cols-2 sm:divide-x sm:divide-y-0">
                <div className="p-6">
                  <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <ShieldCheck size={14} weight="duotone" /> Total warranties (all users)
                  </div>
                  <div className="mt-1 font-head text-4xl font-bold text-riq-primary">{data.total.toLocaleString()}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{data.users_with_warranties} users tracking</div>
                </div>
                <div className="p-6" style={{ background: `${accent}0d` }}>
                  <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider" style={{ color: accent }}>
                    <TrendUp size={14} weight="duotone" /> New added · {periodLabel}
                  </div>
                  <div className="mt-1 font-head text-4xl font-bold" style={{ color: accent }} data-testid="war-new-added">
                    {data.new_added.toLocaleString()}
                  </div>
                  {period !== "all" && (
                    <div className={`mt-1 flex items-center gap-1 text-xs font-medium ${delta >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                      <TrendUp size={12} weight="bold" className={delta < 0 ? "rotate-180" : ""} />
                      {delta >= 0 ? "+" : ""}{delta} vs previous {periodLabel.toLowerCase()}
                    </div>
                  )}
                </div>
              </div>
              <div className="border-t border-riq-border p-5">
                <div className="mb-3 flex items-center gap-2 font-head text-sm font-semibold text-riq-primary">
                  <TrendUp size={16} weight="duotone" /> Added over time
                </div>
                {data.daily.length ? (
                  <ResponsiveContainer width="100%" height={150}>
                    <AreaChart data={data.daily} margin={{ left: -20, right: 4, top: 4, bottom: 0 }}>
                      <defs>
                        <linearGradient id="war-grad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={accent} stopOpacity={0.35} />
                          <stop offset="100%" stopColor={accent} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f4" />
                      <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(d) => d?.slice(5)} minTickGap={24} />
                      <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
                      <Tooltip formatter={(v) => [v, "added"]} />
                      <Area type="monotone" dataKey="count" stroke={accent} strokeWidth={2} fill="url(#war-grad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : <Empty />}
              </div>
            </Card>

            {/* Status donut */}
            <ChartCard title="Status breakdown" icon={Gauge} testId="war-status">
              {data.total > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={170}>
                    <PieChart>
                      <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={72} paddingAngle={2} stroke="none">
                        {statusData.map((s) => <Cell key={s.key} fill={s.color} />)}
                      </Pie>
                      <Tooltip formatter={(v, n) => [v, n]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-2 space-y-2">
                    {statusData.map((s) => (
                      <div key={s.key} data-testid={`war-status-${s.key}`} className="flex items-center gap-2 text-sm">
                        <s.icon size={16} weight="duotone" style={{ color: s.color }} />
                        <span className="flex-1 text-neutral-600">{s.name}</span>
                        <span className="font-semibold text-riq-primary">{s.value}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : <Empty label="No warranties tracked yet." />}
            </ChartCard>
          </div>

          <RankCard
            title={`Most-tracked items · ${periodLabel}`} icon={ShieldCheck} accent={accent} testId="war-top-labels"
            rows={data.top_labels.map((r) => ({ primary: r.label, value: `${r.count}` }))}
            emptyLabel="No warranties added in this period."
          />
        </>
      )}
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ */
/* 4 — Rewards                                                         */
/* ------------------------------------------------------------------ */

function RewardsSection() {
  const { goTab } = useAdminNav();
  const [period, setPeriod] = useState("30d");
  const data = usePeriodData("/admin/rewards/overview", period);
  const accent = "#B36EFF";
  const periodLabel = (FULL_PERIODS.find((p) => p.k === period) || {}).l || "period";

  return (
    <SectionShell
      id="sec-rewards" index={5} icon={Gift} accent={accent}
      title="Rewards" subtitle="Moolas economy and redemption fulfilment"
      period={period} setPeriod={setPeriod} periods={FULL_PERIODS}
    >
      {data === false ? <ErrorBox label="Could not load rewards." /> : !data ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi accent={accent} icon={TrendUp} label="Moolas earned" value={data.stats.total_earned.toLocaleString()} sub="all time" testId="rew-kpi-earned" />
            <Kpi accent={accent} icon={CurrencyCircleDollar} label="Paid out" value={data.stats.total_redeemed.toLocaleString()} sub="redeemed" testId="rew-kpi-paid" />
            <Kpi accent={accent} icon={Wallet} label="Outstanding" value={data.stats.outstanding.toLocaleString()} sub="user balances" testId="rew-kpi-outstanding" />
            <Kpi accent={accent} icon={Trophy} label="Redemptions" value={data.stats.redemptions_count.toLocaleString()} sub={`${data.stats.redemptions_period} in ${periodLabel.toLowerCase()}`} testId="rew-kpi-redemptions" />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {/* Fulfilment status */}
            <div className="grid grid-cols-1 gap-3">
              <button
                data-testid="rew-pending-card"
                onClick={() => goTab("rewards")}
                className="group flex items-center gap-4 rounded-3xl border border-riq-gold/40 bg-riq-gold/10 p-5 text-left transition-transform hover:-translate-y-0.5"
              >
                <div className="grid h-12 w-12 place-items-center rounded-2xl bg-riq-gold/25 text-[#B25E00]">
                  <PaperPlaneTilt size={22} weight="duotone" />
                </div>
                <div className="flex-1">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-[#B25E00]">Pending fulfilment</div>
                  <div className="font-head text-3xl font-bold text-[#B25E00]">{data.stats.pending}</div>
                </div>
                <CaretRight size={18} className="text-[#B25E00] transition-transform group-hover:translate-x-0.5" />
              </button>
              <div className="flex items-center gap-4 rounded-3xl border border-riq-border bg-white p-5">
                <div className="grid h-12 w-12 place-items-center rounded-2xl bg-[#E4F7ED] text-[#22B573]">
                  <CheckCircle size={22} weight="duotone" />
                </div>
                <div className="flex-1">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Fulfilled</div>
                  <div className="font-head text-3xl font-bold text-[#22B573]">{data.stats.fulfilled}</div>
                </div>
              </div>
            </div>

            {/* Redemptions over time */}
            <div className="lg:col-span-2">
              <ChartCard title="Redemptions over time" icon={Coins} testId="rew-daily">
                {data.daily.length ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={data.daily} margin={{ left: -20, right: 4, top: 4, bottom: 0 }}>
                      <defs>
                        <linearGradient id="rew-grad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={accent} stopOpacity={0.35} />
                          <stop offset="100%" stopColor={accent} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f4" />
                      <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(d) => d?.slice(5)} minTickGap={24} />
                      <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
                      <Tooltip formatter={(v, n) => [v, n === "moolas" ? "Moolas" : "redemptions"]} />
                      <Area type="monotone" dataKey="count" stroke={accent} strokeWidth={2} fill="url(#rew-grad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : <Empty label="No redemptions in this period." />}
              </ChartCard>
            </div>
          </div>

          <RankCard
            title={`Top redeemed rewards · ${periodLabel}`} icon={Gift} accent={accent} testId="rew-top-rewards"
            rows={data.top_rewards.map((r) => ({ primary: r.label, secondary: `${r.moolas} Moolas`, value: `${r.count}×` }))}
            emptyLabel="No redemptions in this period."
          />
        </>
      )}
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ */
/* Shared building blocks                                              */
/* ------------------------------------------------------------------ */

function Kpi({ icon: Icon, label, value, sub, accent = "#6E3FFC", testId }) {
  return (
    <Card className="p-4" data-testid={testId}>
      <div className="mb-2 grid h-8 w-8 place-items-center rounded-xl" style={{ background: `${accent}14`, color: accent }}>
        <Icon size={17} weight="duotone" />
      </div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 truncate font-head text-2xl font-bold text-riq-primary">{value}</div>
      {sub && <div className="mt-1 text-[11px] leading-tight text-muted-foreground">{sub}</div>}
    </Card>
  );
}

function ChartCard({ title, icon: Icon, testId, children }) {
  return (
    <Card data-testid={testId}>
      <h3 className="mb-4 flex items-center gap-2 font-head font-semibold text-riq-primary">
        <Icon size={18} weight="duotone" /> {title}
      </h3>
      {children}
    </Card>
  );
}

function TrendCard({ title, data, dataKey, color, icon: Icon }) {
  const id = `${dataKey}-${color}`.replace(/[^a-z0-9]/gi, "");
  return (
    <Card>
      <h3 className="mb-4 flex items-center gap-2 font-head font-semibold text-riq-primary">
        <Icon size={18} weight="duotone" /> {title}
      </h3>
      {data?.length ? (
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={data} margin={{ left: -20, right: 4, top: 4, bottom: 0 }}>
            <defs>
              <linearGradient id={`g-${id}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f4" />
            <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(d) => d?.slice(5)} minTickGap={24} />
            <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
            <Tooltip formatter={(v) => v} />
            <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} fill={`url(#g-${id})`} />
          </AreaChart>
        </ResponsiveContainer>
      ) : <Empty />}
    </Card>
  );
}

function RankCard({ title, icon: Icon, accent = "#6E3FFC", rows, testId, emptyLabel }) {
  return (
    <Card data-testid={testId}>
      <h3 className="mb-4 flex items-center gap-2 font-head font-semibold text-riq-primary">
        <Icon size={18} weight="duotone" /> {title}
      </h3>
      {rows?.length ? (
        <div className="-mx-1 divide-y divide-riq-border">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-3 px-1 py-2.5 text-sm">
              <span
                className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-bold"
                style={{ background: `${accent}1a`, color: accent }}
              >
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                {r.onClick ? (
                  <button data-testid={r.testId} onClick={r.onClick} className="block max-w-full truncate text-left font-medium text-riq-primary hover:underline">
                    {r.primary}
                  </button>
                ) : (
                  <div className="truncate font-medium text-neutral-800">{r.primary}</div>
                )}
                {r.secondary && <div className="truncate text-xs text-muted-foreground">{r.secondary}</div>}
              </div>
              <span className="shrink-0 font-semibold text-riq-primary">{r.value}</span>
            </div>
          ))}
        </div>
      ) : <Empty label={emptyLabel} />}
    </Card>
  );
}

function Empty({ label }) {
  return <div className="py-6 text-center text-sm text-muted-foreground">{label || "No data for this period."}</div>;
}

function ErrorBox({ label }) {
  return <div className="rounded-2xl border border-riq-border bg-white p-6 text-sm text-muted-foreground">{label}</div>;
}

/* ------------------------------------------------------------------ */
/* Fraud watch (kept as a top alert banner)                            */
/* ------------------------------------------------------------------ */

const ANOM_ICON = {
  high_rejection: Warning,
  volume_spike: TrendUp,
  low_confidence: Gauge,
  new_login_area: GlobeHemisphereWest,
};

function AnomalyPanel() {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);

  const load = () => api.get("/admin/anomalies").then((r) => setData(r.data)).catch(() => setData({ anomalies: [], count: 0 }));
  useEffect(() => { load(); }, []);

  const runSweep = async () => {
    setRunning(true);
    try {
      const { data: res } = await api.post("/admin/anomalies/run");
      toast.success(res.new > 0 ? `${res.new} new anomaly(ies) — admins emailed` : "Sweep complete — no new anomalies");
      load();
    } catch {
      toast.error("Could not run the sweep");
    } finally {
      setRunning(false);
    }
  };

  if (!data) return null;
  const clean = data.count === 0;

  return (
    <Card data-testid="anomaly-panel" className={`!p-0 overflow-hidden border ${clean ? "border-emerald-200" : "border-red-200"}`}>
      <div className={`flex items-center gap-3 px-5 py-3.5 ${clean ? "bg-emerald-50" : "bg-red-50"}`}>
        <ShieldWarning size={22} weight="duotone" className={clean ? "text-emerald-600" : "text-red-600"} />
        <div className="min-w-0 flex-1">
          <div className={`font-head font-bold ${clean ? "text-emerald-700" : "text-red-700"}`}>Fraud watch</div>
          <div className="text-xs text-muted-foreground">
            {clean ? "No anomalies right now. All clear." : `${data.count} active · ${data.high} high · ${data.medium} medium`}
          </div>
        </div>
        <button
          data-testid="anomaly-sweep-btn"
          onClick={runSweep}
          disabled={running}
          className="flex items-center gap-1.5 rounded-full border border-riq-border bg-white px-3.5 py-1.5 text-xs font-medium text-neutral-600 hover:border-riq-primary disabled:opacity-50"
        >
          <ArrowClockwise size={13} weight="bold" className={running ? "animate-spin" : ""} /> Run sweep
        </button>
      </div>

      {!clean && (
        <div className="divide-y divide-riq-border">
          {data.anomalies.slice(0, 12).map((a, i) => {
            const Icon = ANOM_ICON[a.type] || Warning;
            const high = a.severity === "high";
            return (
              <div key={i} data-testid={`anomaly-item-${a.type}`} className="flex items-center gap-3 px-5 py-3">
                <div className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${high ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-600"}`}>
                  <Icon size={16} weight="duotone" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-riq-primary">{a.email}</div>
                  <div className="truncate text-xs text-muted-foreground">{a.detail}</div>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${high ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>
                  {a.title}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
